/**
 * Voice-message recording: prefer the connected headphone/headset mic when
 * available, with browser voice processing plus a gentle Web Audio chain
 * (EQ, light compression, soft expander — no hard gate).
 */

const HEADSET_MIC_RE =
  /bluetooth|headset|headphone|hands[\s-]?free|airpods|buds|earphone|earbuds|jabra|plantronics|wh-?\d|bose|sony.?wh|beats|openear|open.?ear/i;

/** Track-level constraints passed to getUserMedia audio */
export const VOICE_AUDIO_TRACK_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
  // Chrome / Chromium extras (ignored when unsupported)
  googEchoCancellation: true,
  googNoiseSuppression: true,
  googAutoGainControl: true,
  googHighpassFilter: true,
  googTypingNoiseDetection: true,
  voiceIsolation: true,
};

export const VOICE_AUDIO_CONSTRAINTS = {
  audio: { ...VOICE_AUDIO_TRACK_CONSTRAINTS },
  video: false,
};

function isHeadsetMic(device) {
  return HEADSET_MIC_RE.test(device?.label || '');
}

/** Prefer headphone/headset mic when one is connected; otherwise first available. */
function pickPreferredMic(inputs) {
  if (!inputs.length) return null;
  const headset = inputs.find(isHeadsetMic);
  if (headset) return headset;
  // Default / communications devices often track the active route
  const namedDefault = inputs.find((d) =>
    /default|communications/i.test(d.label || '')
  );
  return namedDefault || inputs[0];
}

async function ensureMicLabels(inputs) {
  if (inputs.length && inputs.every((d) => !d.label)) {
    const temp = await navigator.mediaDevices.getUserMedia({
      audio: { ...VOICE_AUDIO_TRACK_CONSTRAINTS },
      video: false,
    });
    temp.getTracks().forEach((t) => t.stop());
    return (await navigator.mediaDevices.enumerateDevices()).filter(
      (d) => d.kind === 'audioinput'
    );
  }
  return inputs;
}

async function openRawMicStream(explicitConstraints) {
  if (explicitConstraints) {
    return {
      stream: await navigator.mediaDevices.getUserMedia(explicitConstraints),
      isHeadset: false,
    };
  }

  const fallback = async () => ({
    stream: await navigator.mediaDevices.getUserMedia({
      audio: { ...VOICE_AUDIO_TRACK_CONSTRAINTS },
      video: false,
    }),
    isHeadset: false,
  });

  try {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return fallback();
    }

    let inputs = (await navigator.mediaDevices.enumerateDevices()).filter(
      (d) => d.kind === 'audioinput'
    );
    inputs = await ensureMicLabels(inputs);

    const preferred = pickPreferredMic(inputs);
    const headsetPreferred = preferred ? isHeadsetMic(preferred) : false;

    if (preferred?.deviceId) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            ...VOICE_AUDIO_TRACK_CONSTRAINTS,
            // exact when we intentionally want the headset mic
            deviceId: headsetPreferred
              ? { exact: preferred.deviceId }
              : { ideal: preferred.deviceId },
          },
          video: false,
        });
        return { stream, isHeadset: headsetPreferred };
      } catch (_) {
        if (headsetPreferred) {
          // Retry with ideal if exact failed
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              audio: {
                ...VOICE_AUDIO_TRACK_CONSTRAINTS,
                deviceId: { ideal: preferred.deviceId },
              },
              video: false,
            });
            return { stream, isHeadset: true };
          } catch (__) {
            /* fall through */
          }
        }
      }
    }
  } catch (_) {
    /* fall through */
  }

  const result = await fallback();
  return result;
}

/**
 * EQ + light compression + gentle downward expander (not a hard gate).
 * Soft speech stays open; quiet pauses are only slightly ducked.
 * Returns a new MediaStream; stopping its tracks also stops the raw mic.
 */
function enhanceVoiceStream(rawStream, { isHeadset = false } = {}) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return rawStream;

  let ctx;
  try {
    ctx = new AudioCtx();
  } catch (_) {
    return rawStream;
  }

  const source = ctx.createMediaStreamSource(rawStream);

  // Cut rumble / handling noise without thinning speech
  const highpass = ctx.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = isHeadset ? 100 : 80;
  highpass.Q.value = 0.707;

  // Mild hiss roll-off (keep speech band intact)
  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = 9000;
  lowpass.Q.value = 0.707;

  // Light compression only — heavy ratios pump background noise up
  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -24;
  compressor.knee.value = 30;
  compressor.ratio.value = 3;
  compressor.attack.value = 0.005;
  compressor.release.value = 0.25;

  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.85;

  const expander = ctx.createGain();
  expander.gain.value = 1;

  const dest = ctx.createMediaStreamDestination();

  source.connect(highpass);
  highpass.connect(lowpass);
  lowpass.connect(compressor);
  compressor.connect(expander);
  expander.connect(dest);
  compressor.connect(analyser);

  const timeData = new Float32Array(analyser.fftSize);
  // Low open threshold so soft speech stays at full gain
  const openThreshold = 0.006;
  // Never mute — only gently duck true silence/pauses (~4 dB max)
  const floorGain = 0.65;
  const attackSmooth = 0.04; // fast open — don't clip word starts
  const releaseSmooth = 0.22; // slow close — don't chop soft endings
  let currentGain = 1;
  let rafId = 0;
  let cleaned = false;

  const tick = () => {
    if (cleaned || ctx.state === 'closed') return;
    analyser.getFloatTimeDomainData(timeData);
    let sum = 0;
    for (let i = 0; i < timeData.length; i++) {
      sum += timeData[i] * timeData[i];
    }
    const rms = Math.sqrt(sum / timeData.length);

    let target = 1;
    if (rms < openThreshold) {
      // Continuous expander curve (not binary on/off)
      const x = Math.max(0, rms / openThreshold);
      target = floorGain + (1 - floorGain) * x * x;
    }

    const smooth = target > currentGain ? attackSmooth : releaseSmooth;
    currentGain += (target - currentGain) * Math.min(1, smooth * 6);
    try {
      expander.gain.setTargetAtTime(
        Math.max(floorGain, Math.min(1, currentGain)),
        ctx.currentTime,
        Math.max(0.02, smooth)
      );
    } catch (_) {
      /* ignore */
    }
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);

  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  const outStream = dest.stream;

  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    if (rafId) cancelAnimationFrame(rafId);
    try {
      rawStream.getTracks().forEach((t) => t.stop());
    } catch (_) {
      /* ignore */
    }
    try {
      if (ctx.state !== 'closed') ctx.close();
    } catch (_) {
      /* ignore */
    }
  };

  outStream.getTracks().forEach((track) => {
    track.addEventListener('ended', cleanup);
    const originalStop = track.stop.bind(track);
    track.stop = () => {
      try {
        originalStop();
      } finally {
        cleanup();
      }
    };
  });

  return outStream;
}

/**
 * Open a mic stream optimized for voice messages.
 * Prefers the connected headphone/headset mic and runs a quality-enhancement chain.
 */
export async function getVoiceMediaStream(explicitConstraints) {
  const { stream: rawStream, isHeadset } = await openRawMicStream(
    explicitConstraints
  );
  try {
    return enhanceVoiceStream(rawStream, { isHeadset });
  } catch (_) {
    return rawStream;
  }
}

/** Expose for audio-react-recorder postinstall patch */
export function registerVoiceRecordingGlobals() {
  if (typeof window === 'undefined') return;
  window.__kjuupGetVoiceMediaStream = getVoiceMediaStream;
}
